<template>
  <section id="shipments-to-buyer-registry">
    <VDataGridV2
      :loader="loader"
      :use-filters-cache="false"
      paginated
      :context-menu="ShipmentsToBuyerService.contextMenu"
      @row:dblclick="openShipmentToBuyerCard"
    >
      <VDataGridColumnV2
        id="id"
        label="№"
        width="80px"
        content-align="left"
        sortable
        sort-default-value="desc"
        filterable
        filter-send-name="idPattern"
        :filter-props="{
          placeholder: '№',
          clearable:   true,
        }"
      >
        <template #default="{ row }">
          <VLink @click="openShipmentToBuyerCard(row)">
            {{ $core.utils.string.formatWithDelimiter(row.id) }}
          </VLink>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="seller"
        label="Грузоотправитель"
        width="120px"
        filterable
        filter-identity="*"
        filter-type="select"
        filter-send-name="seller"
        content-class="ellipsis"
        :filter-props="{
          searchFunction: PartnersService.searchSellers,
          placeholder:    'Название партнера',
          clearable:      true,
        }"
      >
        <template #default="{ row }">
          <div
            class="ellipsis"
            :title="row.seller?.name"
          >
            {{ row.seller?.name }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="buyer"
        label="Грузополучатель"
        width="120px"
        filterable
        filter-identity="*"
        filter-type="select"
        filter-send-name="buyer"
        content-class="ellipsis"
        :filter-props="{
          searchFunction: PartnersService.searchBuyers,
          placeholder:    'Название партнера',
          clearable:      true,
        }"
      >
        <template #default="{ row }">
          <div
            class="ellipsis"
            :title="row.buyer?.name"
          >
            {{ row.buyer?.name }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="sumFull"
        label="Сумма"
        width="120px"
        content-align="right"
      >
        <template #default="{ row }">
          {{ row.sumFull.format(row.orderCurrency) || '---' }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="accountingSumFull"
        label="Сумма в валюте учёта"
        width="180px"
        content-align="right"
        header-class="nowrap"
      >
        <template #default="{ row }">
          {{ row.accountingSumFull.format(row.accountingCurrency) || '---' }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="accountingVatSum"
        label="Сумма НДС в валюте учёта"
        width="180px"
        content-align="right"
      >
        <template #default="{ row }">
          {{ row.accountingVatSum.format(row.accountingCurrency) || '---' }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="sftPriceMod"
        label="Тех наценка"
        width="80px"
        content-align="right"
        header-class="nowrap"
      >
        <template #default="{ row }">
          <div :class="{ warning: row.sftPriceMod.greaterThan(row.priceModActual) }">
            {{ row.sftPriceMod?.toString() }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="priceModActual"
        label="Наценка полная"
        width="80px"
        content-align="right"
        header-class="nowrap"
      >
        <template #default="{ row }">
          <div :class="{ warning: row.sftPriceMod.greaterThan(row.priceModActual) }">
          {{ row.priceModActual.toString() }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        label="НДС"
        width="70px"
        content-align="center"
      >
        <template #default="{ row }">
          <div :class="{ warning: isVatRateUnexpected(row) }">
            {{ row.vatRateActual }}
          </div>
        </template>

      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="rate"
        label="Курс"
        width="80px"
        content-align="right"
      >
        <template #default="{ row }">
          {{ row.accountingCurrencyRate.format() }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="shipmentDate"
        label="Создан"
        width="100px"
        content-align="right"
        filterable
        filter-type="date-range"
        filter-send-name="period"
        :filter-props="{
          placeholder: 'Введите диапазон',
          clearable:   true,
        }"
      >
        <template #default="{ row }">
          {{ row.shipmentDate?.toLocalString() }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="shipmentFromSeller"
        label="Приобретение"
        width="100px"
        content-align="center"
        filterable
        filter-send-name="shipmentDocumentNumberPattern"
        :filter-props="{
          placeholder: '№ основания',
          clearable:   true,
        }"
      >
        <template #default="{ row }">
          <template
            v-for="(part, index) in row.shipmentFromSeller?.toString()
      .replace(/\s+от\s+/i, '\nот ')
      .replace(/\s+(\((?:не )?сверен\))$/i, '\n$1')
      .split('\n')"
            :key="index"
          >
            <VLink
              v-if="index === 0"
              @click="ShipmentsService.showShipmentCard(row)"
            >
              {{ part }}
            </VLink>

            <div v-else class="description">
              {{ part }}
            </div>
          </template>
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>
  </section>
</template>

<script lang="ts">

import DataGridLoader          from '@/lib/components/VDataGridV2/DataGridLoader';
import ShipmentsToBuyerService from '@/domains/ShipmentsToBuyer/ShipmentsToBuyerService';
import ShipmentsService        from "@/domains/Shipments/ShipmentsService";

import CurrenciesService       from "@/domains/Currencies/CurrenciesService";
import PartnersService         from "@/domains/Partners/PartnersService";

import type Shipment           from '@/domains/Shipments/Shipment';
import type ShipmentToBuyer    from '@/domains/ShipmentsToBuyer/ShipmentToBuyer';

export default defineComponent({
  name: 'ShipmentsToBuyerRegistry',

  props: {
    /**
     * Отгрузка от поставщика — основание.
     * Если передана, реестр показывает только связанные с ней реализации.
     */
    shipmentFromSeller: {
      type: Object as PropType<Shipment>,
      default: null,
    },
  },

  setup() {
    return {
      PartnersService,
      ShipmentsService,
      CurrenciesService,
      ShipmentsToBuyerService,
    }
  },

  data() {
    return {
      loader: new DataGridLoader(ShipmentsToBuyerService.getListPaginated)
        .useReactiveSource(() => ({
          shipmentFromSeller: this.shipmentFromSeller,
        })),
    };
  },

  methods: {
    /** Правило сравнения со ставкой из конфига живёт в сервисе - документ
     *  про ожидаемую ставку не знает. */
    isVatRateUnexpected(shipmentToBuyer: ShipmentToBuyer): boolean {
      return ShipmentsToBuyerService.isVatRateUnexpected(shipmentToBuyer);
    },

    /** Реализации пока не редактируются, но карточка возвращает признак
     *  изменения — обновляем реестр, как в реестре сборок. */
    async openShipmentToBuyerCard(shipmentToBuyer: ShipmentToBuyer): Promise<void> {
      if (await ShipmentsToBuyerService.showShipmentToBuyerCard(shipmentToBuyer.id) === true) {
        this.loader.refresh();
      }
    },
  },
});
</script>
